import Request from './luch-request/index.js';
import globalConfig from './config.js';
// import projectConfig from '@project-config';
import {
  parseURL,
  generateSign,
  filterObjectNullVal,
  apiErrorCodeHandling,
  httpCodeHandling
} from './utils';

const mergedConfig = {
  ...globalConfig
  // ...(projectConfig || {})
};

const http = new Request();

http.setConfig((config) => {
  config.baseURL = mergedConfig.BASE_API;
  return config;
});

const pendingRequests = new Map();

// 取消请求
const cancelRequest = (requestTask) => {
  if (requestTask && typeof requestTask.abort === 'function') {
    requestTask.abort();
  }
};
const generateRequestKey = (config, includeData = true) => {
  const { url } = config;
  let key = `${url}`;

  // 如果需要更精确的控制，可以将请求参数也纳入标识计算
  if (includeData) {
    const data = config.data || config.formData || {};
    key = `${key}${JSON.stringify(data)}`;
  }

  return key;
};

const cancelPendingRequest = (requestKey) => {
  if (pendingRequests.has(requestKey)) {
    const requestTask = pendingRequests.get(requestKey);
    cancelRequest(requestTask);
    pendingRequests.delete(requestKey);
  }
};

const cancelAllPendingRequests = (message = '请求已取消') => {
  pendingRequests.forEach((requestTask, key) => {
    cancelRequest(requestTask, message);
  });
  pendingRequests.clear();
};

// 添加请求拦截器，可指定优先级
const addRequestInterceptor = (onFulfilled, onRejected, options = {}) => {
  const id = http.interceptors.request.use(onFulfilled, onRejected, options);
  return id;
};

// 添加响应拦截器，可指定优先级
const addResponseInterceptor = (onFulfilled, onRejected, options = {}) => {
  const id = http.interceptors.response.use(onFulfilled, onRejected, options);
  return id;
};

/**
 * 竞态控制拦截器 - 处理请求竞态问题
 * 在发送请求前检查是否有相同的请求正在进行，如有则取消旧请求
 */
addRequestInterceptor((config) => {
  // 如果配置了不进行竞态控制，则跳过
  if (config.noCancelDuplicated) {
    return config;
  }
  // 生成请求标识符
  const requestKey = generateRequestKey(config, config.includeDateInKey);
  // 如果存在相同的请求，则取消旧请求
  cancelPendingRequest(requestKey);
  // 保存原始的 getTask 回调
  const originalGetTask = config.getTask;
  // 设置新的 getTask 回调
  config.getTask = (task, options) => {
    // 将任务对象直接存储到 pendingRequests 中
    pendingRequests.set(requestKey, task);
    // 调用原始的 getTask 回调（如果有）
    if (typeof originalGetTask === 'function') {
      originalGetTask(task, options);
    }
    return task;
  };
  // 添加请求完成后的清理函数
  config._requestKey = requestKey;
  return config;
});

/**
 * 竞态控制响应拦截器 - 请求完成后清理
 */
addResponseInterceptor(
  (response) => {
    // 请求成功后，从 pendingRequests 中移除
    if (response.config && response.config._requestKey) {
      pendingRequests.delete(response.config._requestKey);
    }
    return response;
  },
  (error) => {
    // 请求失败后，从 pendingRequests 中移除
    if (error.config && error.config._requestKey) {
      pendingRequests.delete(error.config._requestKey);
    }
    return Promise.reject(error);
  }
);

/**
 * 默认请求拦截器 - 处理签名和参数
 */
addRequestInterceptor((config) => {
  try {
    const isUploadRequest = Boolean(config.formData);
    let dataKeyName = isUploadRequest ? 'formData' : 'data';
    const data = {
      ...config[dataKeyName],
      ...config['params'],
      ...parseURL(config.url)
    };

    // 生成签名
    config.header.Sign = generateSign(data);
    config[dataKeyName] = filterObjectNullVal(data);
    return config;
  } catch (error) {
    console.error('请求处理错误:', error);
    return config;
  }
});

/**
 * 默认响应拦截器 - 处理响应数据和错误
 */
addResponseInterceptor(
  (response) => {
    // 服务端返回的状态码不等于200，则reject()
    if (response.statusCode !== 200) {
      return Promise.reject(response);
    }
    if (typeof response.data === 'string') {
      response.data = JSON.parse(response.data);
    }
    // 如果接口错误码不为0，则进行错误处理
    // const isInvalidResult = response.data.error_code !== 0;
    if (typeof response.data === 'object') {
      const isInvalidResult = response.data.code && response.data.code !== 200;
      if (isInvalidResult) {
        apiErrorCodeHandling(response);
        return Promise.reject(response);
      }
      if (!response.data.result) return response.data.result;

      return {
        ...response.data.result,
        timestamp: response.data.timestamp
      };
    } else {
      return response.data;
    }
  },
  (httpStatusError) => {
    httpCodeHandling(httpStatusError);
    return Promise.reject(httpStatusError);
  },
  { priority: 0 }
);

export {
  http,
  addRequestInterceptor,
  addResponseInterceptor,
  cancelPendingRequest,
  cancelAllPendingRequests
};
